/**
 * Proposing an edit of the AI vocabulary (contract `DashboardAIVocabularyEdit`): a new entry (`create`, possibly
 * started from a derived entry: "Define your own"), a change of one the company or support wrote (`update`: only the
 * fields that change are sent), or its removal (`delete`), each with a reason. The page posts it; a refusal stays in the
 * dialog, in words (`proposeErrorText`), and nothing changed.
 *
 * The binding editor shows the keys the backend lists (`binding_keys`) with its words; staff roles and regions are
 * picked from the company's own (`RemoteAutocomplete`, scoped to the operator the page shows on a franchise), the rest
 * are codes the backend checks against the company's set-up. A key the editor does not know is kept as it is.
 */
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Grid,
    TextField,
    Typography,
} from '@mui/material';
import * as React from 'react';

import FormLicenseeContext from '../../components/FormLicenseeContext';
import { RemoteAutocomplete } from '../../components/RemoteAutocomplete';
import { UntrustedText } from '../../components/UntrustedText';
import { ContextStore } from '../../store/contextStore';
import { CHANNEL_NAMES, proposeErrorText } from './vocabularyWords';

const MAX_TERM = 200;
const MAX_DEFINITION = 1000;
const MAX_REASON = 4000;
const MIN_REASON = 3;
const MAX_ALIASES = 20;
const COUNTRY = /^[A-Z]{2}$/;
const KNOWN_KEYS = ['role_ids', 'region_ids', 'countries', 'tag_category', 'booking_system', 'channel', 'language', 'default'];

const splitList = (text) => String(text || '').split(',').map((s) => s.trim()).filter(Boolean);

const namedRows = (ids, names) => (Array.isArray(ids) ? ids : []).map((id) => ({
    id,
    name: (names || []).find((n) => n.id === id)?.name || `#${id}`,
}));

/** The form's starting values from an entry (or a derived entry to define one's own from). */
const initialForm = (source) => {
    const binding = source?.binding || {};
    const names = source?.binding_names || {};
    return {
        term: source?.term || '',
        aliasesText: (source?.aliases || []).join(', '),
        concept: source?.concept || '',
        roles: namedRows(binding.role_ids, names.role_ids),
        regions: namedRows(binding.region_ids, names.region_ids),
        countriesText: (binding.countries || []).join(', '),
        tagCategory: binding.tag_category || '',
        bookingSystem: binding.booking_system || '',
        channel: binding.channel || '',
        language: binding.language || '',
        isDefault: !!binding.default,
        definition: source?.definition || '',
        reason: '',
    };
};

/** The binding the form says (`base`: the entry's, whose unknown keys are kept). */
const formBinding = (form, base) => {
    const out = {};
    Object.entries(base || {}).forEach(([key, value]) => {
        if (!KNOWN_KEYS.includes(key)) out[key] = value;
    });
    if (form.roles.length) out.role_ids = form.roles.map((r) => r.id);
    if (form.regions.length) out.region_ids = form.regions.map((r) => r.id);
    const countries = splitList(form.countriesText).map((c) => c.toUpperCase());
    if (countries.length) out.countries = countries;
    if (form.tagCategory.trim()) out.tag_category = form.tagCategory.trim();
    if (form.bookingSystem.trim()) out.booking_system = form.bookingSystem.trim();
    if (form.channel) out.channel = form.channel;
    if (form.language.trim()) {
        out.language = form.language.trim();
        if (form.isDefault) out.default = true;
    }
    return out;
};

/** A binding in one canonical shape, to tell whether it changed. */
const canonical = (binding) => JSON.stringify(Object.keys(binding || {}).sort().map((key) => {
    const value = binding[key];
    return [key, Array.isArray(value) ? [...value].sort() : value];
}));

/** What the form asks for: `{ fields, problems }` (`fields` null when nothing changed). */
const buildFields = (mode, form, entry, initialBinding) => {
    const problems = {};
    const aliases = splitList(form.aliasesText);
    if (aliases.length > MAX_ALIASES) problems.aliases = `At most ${MAX_ALIASES} other words.`;
    if (aliases.some((a) => a.length > MAX_TERM)) problems.aliases = `Each other word has at most ${MAX_TERM} characters.`;
    if (splitList(form.countriesText).some((c) => !COUNTRY.test(c.toUpperCase()))) {
        problems.countries = 'Two-letter country codes (ISO), e.g. PE, CL.';
    }
    const term = form.term.trim();
    const definition = form.definition.trim();
    const binding = formBinding(form, entry?.binding);
    if (mode === 'create') {
        if (!term) problems.term = 'Required.';
        if (!form.concept) problems.concept = 'Required.';
        return {
            problems,
            fields: {
                term,
                concept: form.concept,
                ...(aliases.length ? { aliases } : {}),
                ...(Object.keys(binding).length ? { binding } : {}),
                ...(definition ? { definition } : {}),
            },
        };
    }
    if (!term) problems.term = 'Required.';
    if (!form.concept) problems.concept = 'Required.';
    const fields = {};
    if (term !== entry.term) fields.term = term;
    if (form.concept !== entry.concept) fields.concept = form.concept;
    if (JSON.stringify(aliases) !== JSON.stringify(entry.aliases || [])) fields.aliases = aliases;
    if (canonical(binding) !== canonical(initialBinding)) fields.binding = binding;
    if (definition !== (entry.definition || '')) fields.definition = definition;
    return { problems, fields: Object.keys(fields).length ? fields : null };
};

const ConceptSelect = ({ concepts, value, onChange, error }) => {
    const groups = [['entity', 'Things'], ['topic', 'Topics']];
    return (
        <TextField
            select
            required
            fullWidth
            label="Stands for"
            value={value}
            onChange={(ev) => onChange(ev.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            error={!!error}
            helperText="The concept of ours the word means."
        >
            <option value="" />
            {groups.map(([kind, label]) => {
                const options = (concepts || []).filter((c) => c.kind === kind);
                return options.length ? (
                    <optgroup key={kind} label={label}>
                        {options.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                    </optgroup>
                ) : null;
            })}
            {/* a concept the list no longer has (an older entry) stays selectable */}
            {value && !(concepts || []).some((c) => c.key === value) && <option value={value}>{value}</option>}
        </TextField>
    );
};

const BindingFields = ({ form, set, keys, problems, tourOperatorId }) => {
    const { isFranchise } = React.useContext(ContextStore) || {};
    const has = (key) => Object.prototype.hasOwnProperty.call(keys || {}, key);
    const help = (key) => keys?.[key];
    return (
        <FormLicenseeContext.Provider value={isFranchise ? tourOperatorId : null}>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>What it means in your set-up (optional)</Typography>
            <Grid container spacing={2} sx={{ mt: 0 }}>
                {has('role_ids') && (
                    <Grid item xs={12} sm={6}>
                        <RemoteAutocomplete
                            multiple
                            filterSelected
                            resource="employee_role"
                            urlSuffix={null}
                            label="Staff roles"
                            size="small"
                            fullWidth
                            value={form.roles}
                            onChange={(_ev, nv) => set({ roles: nv || [] })}
                        />
                    </Grid>
                )}
                {has('region_ids') && (
                    <Grid item xs={12} sm={6}>
                        <RemoteAutocomplete
                            multiple
                            filterSelected
                            resource="region"
                            urlSuffix={null}
                            label="Regions"
                            size="small"
                            fullWidth
                            value={form.regions}
                            onChange={(_ev, nv) => set({ regions: nv || [] })}
                        />
                    </Grid>
                )}
                {has('countries') && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Countries (ISO codes, comma-separated)"
                            value={form.countriesText}
                            onChange={(ev) => set({ countriesText: ev.target.value })}
                            error={!!problems.countries}
                            helperText={problems.countries || help('countries')}
                        />
                    </Grid>
                )}
                {has('tag_category') && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Tag category (code)"
                            value={form.tagCategory}
                            onChange={(ev) => set({ tagCategory: ev.target.value })}
                            helperText={help('tag_category')}
                        />
                    </Grid>
                )}
                {has('booking_system') && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Booking system (code)"
                            value={form.bookingSystem}
                            onChange={(ev) => set({ bookingSystem: ev.target.value })}
                            helperText={help('booking_system')}
                        />
                    </Grid>
                )}
                {has('channel') && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Channel"
                            value={form.channel}
                            onChange={(ev) => set({ channel: ev.target.value })}
                            SelectProps={{ native: true }}
                            InputLabelProps={{ shrink: true }}
                            helperText={help('channel')}
                        >
                            <option value="">None</option>
                            {Object.entries(CHANNEL_NAMES).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                            {form.channel && !CHANNEL_NAMES[form.channel] && <option value={form.channel}>{form.channel}</option>}
                        </TextField>
                    </Grid>
                )}
                {has('language') && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Language (code)"
                            value={form.language}
                            onChange={(ev) => set({ language: ev.target.value })}
                            helperText={help('language')}
                        />
                        {has('default') && (
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={form.isDefault}
                                        disabled={!form.language.trim()}
                                        onChange={(ev) => set({ isDefault: ev.target.checked })}
                                    />
                                )}
                                label="The default language"
                            />
                        )}
                    </Grid>
                )}
            </Grid>
        </FormLicenseeContext.Provider>
    );
};

/**
 * `mode`: create | update | delete. `entry`: the entry an update or removal acts on; `prefill`: a derived entry a new
 * one starts from. `data`: the page's `DashboardAIVocabulary`. `onSubmit({action, entry, fields, reason})` resolves
 * when proposed (the page closes the dialog) and rejects with the API error (shown here).
 */
const VocabularyEditDialog = ({ mode, entry, prefill, data, onCancel, onSubmit }) => {
    const name = data.tour_operator.name;
    const source = mode === 'create' ? prefill : entry;
    const [form, setForm] = React.useState(() => initialForm(source));
    const initialBinding = React.useMemo(() => formBinding(initialForm(source), source?.binding), [source]);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const { fields, problems } = mode === 'delete' ? { fields: null, problems: {} } : buildFields(mode, form, entry, initialBinding);
    const reasonOk = form.reason.trim().length >= MIN_REASON;
    const unchanged = mode === 'update' && !fields;
    const ready = reasonOk && !busy && Object.keys(problems).length === 0 && !unchanged;

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await onSubmit({
                action: mode,
                ...(mode !== 'create' ? { entry: entry.id } : {}),
                ...(mode !== 'delete' ? { fields } : {}),
                reason: form.reason,
            });
        } catch (e) {
            const extra = (Array.isArray(e.body?.errors) ? e.body.errors : []).filter((t) => t && t !== e.message);
            setError({ text: proposeErrorText(e, name), extra });
            setBusy(false);
        }
    };

    const title = {
        create: 'New entry',
        update: 'Change an entry',
        delete: `Remove “${entry?.term}” from the AI vocabulary?`,
    }[mode];

    return (
        <Dialog open onClose={busy ? undefined : onCancel} maxWidth="md" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText variant="body2" sx={{ mb: 2 }}>
                    {`Your edit is a proposal for ${name}: it applies at once on a sandbox, with auto-apply, or when you may `
                        + 'approve it yourself and signed in with two-step sign-in; otherwise it waits in AI changes › Approvals. '
                        + 'Nothing guests see changes.'}
                </DialogContentText>
                {mode === 'delete' ? (
                    <Box sx={{ mb: 2 }}>
                        <UntrustedText label="Its definition" text={entry.definition} empty="No definition." />
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Term"
                                    value={form.term}
                                    onChange={(ev) => set({ term: ev.target.value })}
                                    inputProps={{ maxLength: MAX_TERM }}
                                    helperText="The word your team uses."
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ConceptSelect
                                    concepts={data.concepts}
                                    value={form.concept}
                                    onChange={(concept) => set({ concept })}
                                    error={problems.concept && form.term.trim() ? problems.concept : null}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Other words (comma-separated)"
                                    value={form.aliasesText}
                                    onChange={(ev) => set({ aliasesText: ev.target.value })}
                                    error={!!problems.aliases}
                                    helperText={problems.aliases || 'Other spellings or words for the same thing.'}
                                />
                            </Grid>
                        </Grid>
                        <BindingFields
                            form={form}
                            set={set}
                            keys={data.binding_keys}
                            problems={problems}
                            tourOperatorId={data.tour_operator.id}
                        />
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            sx={{ mt: 2 }}
                            label="Definition"
                            value={form.definition}
                            onChange={(ev) => set({ definition: ev.target.value })}
                            inputProps={{ maxLength: MAX_DEFINITION }}
                            helperText="What the word means for your company, in a sentence or two."
                        />
                    </>
                )}
                <TextField
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mt: 2 }}
                    label="Reason (recorded with the change)"
                    value={form.reason}
                    onChange={(ev) => set({ reason: ev.target.value })}
                    inputProps={{ maxLength: MAX_REASON }}
                />
                {unchanged && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Nothing changed yet.</Typography>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="body2">{error.text}</Typography>
                        {error.extra.map((t) => <Typography key={t} variant="body2">{t}</Typography>)}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                {busy && <CircularProgress size={20} />}
                <Button onClick={onCancel} disabled={busy}>Cancel</Button>
                <Button variant="contained" color={mode === 'delete' ? 'error' : 'primary'} disabled={!ready} onClick={submit}>
                    {mode === 'delete' ? 'Propose removal' : 'Propose'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VocabularyEditDialog;
